import { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  message,
  Upload,
  Button,
  Image,
  Select,
  Row,
  Col,
} from "antd";
import { ShowToast, Severty } from "../../helper/toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { uploadFileToS3 } from "../../helper/uploadImage";
import { UploadOutlined } from "@ant-design/icons";
import useApi from "../../hooks/useApi";
import {
  ListBranch,
  getEmployee,
  updateEmployee,
} from "../../constants/API/API";
import callApi from "../../constants/API/CallApi";
import { emailDomainValidation } from "../../helper/functions";

const EmployeeUpdate = ({
  editVisible,
  setEditVisible,
  selectedEmployee: userId,
  refresh,
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [branches, setBranches] = useState([]);

  useApi({
    callback: getEmployee,
    payload: userId,
    dependencies: [userId],
    shouldCallApi: Boolean(userId),
    onSuccess: (data) => {
      setSelectedEmployee(data);
    },
  });

  useEffect(() => {
    async function fetchBranches() {
      try {
        const res = await ListBranch();
        const activeBranches = (res.data.list || []).filter(
          (branch) => branch.isActive
        );
        setBranches(activeBranches);
      } catch (e) {
        setBranches([]);
      }
    }
    fetchBranches();
  }, []);

  const FileType = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/avif",
    "image/webp",
    "image/gif",
  ];
  const [file, setFile] = useState([]);
  const [image, setImage] = useState([]);
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState({
    mobileNumber: "",
    countryCode: "",
  });

  useEffect(() => {
    if (selectedEmployee && editVisible) {
      const mobileNumber = selectedEmployee.mobileNumber || "";
      const profilePic = selectedEmployee.profilePic || "";
      const countryCode = selectedEmployee.countryCode || "";

      setInitialValues({
        ...selectedEmployee,
        mobileNumber: mobileNumber,
      });

      setPhone({
        mobileNumber,
        countryCode,
      });

      form.setFieldsValue({
        name: selectedEmployee?.name || "",
        email: selectedEmployee?.email || "",
        mobileNumber: `${countryCode}${mobileNumber}`,
        profilePic: profilePic,
        role: selectedEmployee?.role || "",
        branchId: selectedEmployee?.branchId?._id || undefined,
      });

      if (profilePic) {
        setImage(profilePic);
        setFile([profilePic]);
      }
    }
  }, [selectedEmployee, form, editVisible]);

  const handleOk = async (values) => {
    setLoading(true);
    callApi({
      callback: updateEmployee,
      payload: [
        {
          ...values,
          ...phone,
          roleType: "employee",
          profilePic: file.length > 0 ? file[0].url : null,
        },
        selectedEmployee._id,
      ],
      onSuccess: ({ message }) => {
        refresh(new Date().getTime());
        ShowToast(message, Severty.SUCCESS);
        setEditVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setInitialValues((prev) => ({}));
    setPhone((prev) => ({}));
    setEditVisible(false);
  };

  const validateName = (rule, value) => {
    if (!value) {
      return Promise.reject("Please enter a name");
    } else if (!/^[A-Z][a-zA-Z ]*$/.test(value)) {
      return Promise.reject(
        "Name must start with a capital letter and contain only letters"
      );
    } else {
      return Promise.resolve();
    }
  };

  const handleChangeMobileNumber = (value, country) => {
    const { dialCode } = country;
    const mobileNumber = value.slice(dialCode?.length);
    setPhone({
      mobileNumber: mobileNumber,
      countryCode: dialCode,
    });
  };

  const beforeUpload = (file) => {
    if (FileType.includes(file.type)) {
    } else {
      message.error("File format is not correct");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 5;
    if (!isLt2M) {
      message.error(`Image must be smaller than 5 MB!`);
      return false;
    }
    return true;
  };

  const handleChange = async (event) => {
    const { file } = event;

    setLoading(true);
    uploadFileToS3({ file, dirName: "dojo/profile" })
      .then((data) => {
        const fileData = {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: data.Location,
          thumbUrl: data.Location,
        };
        setFile([fileData]);
        setImage(data.Location);
        form.setFieldsValue({ image: data.Location });
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleRemove = async () => {
    setFile([]);
    setImage("");
  };

  return (
    <Modal
      width={800}
      centered
      open={editVisible}
      className="tab_modal"
      title="Update Employee"
      okButtonProps={{
        htmlType: "submit",
        form: "edit-employee",
        loading: loading,
      }}
      onCancel={handleCancel}
      afterClose={() => form.resetFields()}
    >
      <Form
        id="edit-employee"
        onFinish={handleOk}
        form={form}
        layout="vertical"
        initialValues={initialValues}
      >
        <Row gutter={12}>
          <Col span={24} md={12}>
            <Form.Item
              name="name"
              label="Employee Name"
              rules={[
                { required: true, message: "Please enter the name" },
                { validator: validateName },
              ]}
              normalize={(v) => v?.trimStart()}
            >
              <Input placeholder="Enter name" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: "Please select role!" }]}
            >
              <Select placeholder="Select Role Type">
                <Select.Option key={"branch"}>Admin</Select.Option>
                <Select.Option key={"trainer"}>Trainer</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select branch!" }]}
            >
              <Select placeholder="Select Branch">
                {branches?.map((item) => (
                  <Select.Option key={item?._id}>{item?.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter the email" },
                // emailDomainValidation,
              ]}
              normalize={(v) => v?.trim()}
            >
              <Input placeholder="Enter email" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="mobileNumber"
              label="Mobile Number"
              rules={[
                { required: true, message: "Please enter the mobile number" },
              ]}
            >
              <PhoneInput
                countryCode="91"
                countryCodeEditable={false}
                inputProps={{
                  name: "mobile",
                  required: true,
                  autoFocus: false,
                }}
                isValid={(value, country) => {
                  if (value.match(/1234/)) {
                    return "Invalid value: " + value + ", " + country.name;
                  } else if (value.match(/1234/)) {
                    return "Invalid value: " + value + ", " + country.name;
                  } else {
                    return true;
                  }
                }}
                country={"ca"}
                value={
                  phone
                    ? (phone.countryCode ? phone.countryCode : "+1") +
                      (phone.mobileNumber ? phone.mobileNumber : null)
                    : ""
                }
                onChange={handleChangeMobileNumber}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* <Form.Item className="" label="Upload Profile" name="profilePic">
          <Upload
            listType="picture"
            maxCount={1}
            beforeUpload={beforeUpload}
            customRequest={handleChange}
            onRemove={handleRemove}
            fileList={file}
            action=""
          >
            {file && file.length > 0 ? null : (
              <Button icon={<UploadOutlined />}>Upload</Button>
            )}
          </Upload>

          {image && (
            <div className="mt-3">
              <Image width={300} src={image}></Image>
            </div>
          )}
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default EmployeeUpdate;
